import React, { useState } from "react";
import { motion } from "framer-motion";
import { FileCheck2, Timer, TrendingUp, Building2 } from "lucide-react";
import Modal, { Ext } from "./Modal";

const cards = [
  {
    icon: FileCheck2,
    title: "¿Qué es el RETEF?",
    text: "Registro de Extractores de Depósitos Fiscales concedido por la AEAT (Real Decreto 249/2023). Permite extraer productos de depósitos fiscales operando en régimen suspensivo de impuestos especiales.",
    tag: "RD 249/2023",
    modal: (
      <>
        <p>El RETEF (Registro de Extractores de Depósitos Fiscales) es el registro administrativo creado por el Real Decreto 249/2023, de 4 de abril, que modifica el Reglamento de los Impuestos Especiales (RD 1165/1995). Permite a las empresas registradas actuar como extractores autorizados de productos almacenados en depósitos fiscales, operando en régimen suspensivo de impuestos especiales.</p>
        <h4>¿Qué permite?</h4>
        <ul>
          <li>Extraer productos de un depósito fiscal sin liquidar el impuesto en el momento</li>
          <li>Actuar como extractor por cuenta de terceros</li>
          <li>Diferir el impuesto hasta la puesta a consumo</li>
          <li>Operar con total seguridad jurídica ante la AEAT</li>
        </ul>
        <h4>Marco normativo</h4>
        <ul>
          <li><Ext href="https://www.boe.es/diario_boe/txt.php?id=BOE-A-2023-8993">Real Decreto 249/2023</Ext></li>
          <li><Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1995-17640">Real Decreto 1165/1995 (Reglamento IIEE)</Ext></li>
          <li><Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1992-26834">Ley 38/1992 de Impuestos Especiales</Ext></li>
        </ul>
        <p>FiscalBond es titular del RETEF <strong>Nº 004VBSG6OC001684166</strong>, verificable en sede electrónica de la AEAT.</p>
      </>
    ),
  },
  {
    icon: Timer,
    title: "¿Qué es el régimen suspensivo?",
    text: "Los impuestos especiales quedan en suspenso mientras el producto permanece en el depósito fiscal. Solo se liquidan cuando el producto sale al consumo, liberando liquidez financiera.",
    tag: "Ley 38/1992",
    modal: (
      <>
        <p>El régimen suspensivo es el mecanismo fiscal por el que los impuestos especiales quedan en suspenso mientras el producto se encuentra en un depósito fiscal autorizado o se transporta entre depósitos dentro de la UE. <strong>El impuesto NO se devenga hasta que el producto sale al consumo.</strong></p>
        <h4>Ventajas financieras</h4>
        <ul>
          <li>El impuesto sobre el alcohol puede superar 830€ por hectolitro de alcohol puro</li>
          <li>El impuesto sobre hidrocarburos supera 0,40€/litro en gasolina</li>
          <li>Diferirlo hasta la venta supone una ventaja de tesorería enorme</li>
        </ul>
        <h4>Marco normativo</h4>
        <ul>
          <li><Ext href="https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=CELEX%3A32020L0262">Directiva 2020/262/UE</Ext></li>
          <li><Ext href="https://www.boe.es/buscar/act.php?id=BOE-A-1992-26834">Ley 38/1992, arts. 4 y siguientes</Ext></li>
          <li><Ext href="https://eur-lex.europa.eu/legal-content/ES/TXT/?uri=CELEX%3A32013R0952">Reglamento UE 952/2013 (Código Aduanero)</Ext></li>
        </ul>
      </>
    ),
  },
  {
    icon: TrendingUp,
    title: "¿Por qué es tan valioso?",
    text: "El impuesto sobre hidrocarburos y sobre alcohol representa una parte enorme del precio del producto. Diferirlo supone una ventaja financiera y competitiva enorme.",
    tag: "Ventaja financiera",
    modal: (
      <>
        <p>Los impuestos especiales representan uno de los mayores costes en las cadenas de suministro de alcohol e hidrocarburos.</p>
        <h4>Tipos impositivos vigentes</h4>
        <ul>
          <li>Alcohol etílico: 830,25 €/hl de alcohol puro</li>
          <li>Bebidas espirituosas: 10,18 €/litro de alcohol puro</li>
          <li>Gasoil automoción: 307,00 €/1.000 litros</li>
          <li>Gasolina sin plomo 95: 400,69 €/1.000 litros</li>
        </ul>
        <h4>Ejemplo real</h4>
        <p>Una importación de 10.000 litros de whisky a 40% vol. contiene 4.000 litros de alcohol puro. El impuesto especial sería <strong>40.720€</strong>. En régimen suspensivo, ese importe queda disponible en tesorería hasta la venta.</p>
      </>
    ),
  },
  {
    icon: Building2,
    title: "¿Quién puede aprovecharlo?",
    text: "Cualquier empresa del sector que opere con productos sujetos a impuestos especiales y que no disponga de RETEF propio: importadores, distribuidores, trading, industria.",
    tag: "Sin RETEF propio",
    modal: (
      <>
        <p>Cualquier empresa que opere con productos sujetos a impuestos especiales puede beneficiarse usando FiscalBond como extractor autorizado:</p>
        <ul>
          <li>Importadores de vino, cerveza, espirituosos o alcohol industrial</li>
          <li>Distribuidores mayoristas de bebidas o hidrocarburos</li>
          <li>Empresas de trading intracomunitario de alcohol o combustibles</li>
          <li>Industria que usa alcohol como materia prima (farmacia, cosmética, alimentación)</li>
          <li>Distribuidoras de gasóleo agrícola o de calefacción</li>
          <li>Empresas con operaciones triangulares UE → España → terceros países</li>
        </ul>
        <h4>No necesitas</h4>
        <p>✓ RETEF propio (FiscalBond actúa por tu cuenta)<br />✓ Constituir garantías ante la AEAT<br />✓ Conocer la normativa técnica de impuestos especiales<br />✓ Depósito fiscal propio</p>
      </>
    ),
  },
];

export const RetefInfo = () => {
  const [open, setOpen] = useState(null);
  return (
    <section
      id="retef"
      data-testid="retef-section"
      className="relative bg-[#0A1628] py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute top-1/3 -right-32 w-[400px] h-[400px] rounded-full bg-[#0066FF]/10 blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-[#FF6B00]/10 blur-[100px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#FF6B00] font-semibold">
            Marco regulatorio
          </span>
          <h2
            className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight"
            style={{ fontFamily: "Poppins" }}
          >
            ¿Qué es el RETEF y el régimen suspensivo?
          </h2>
          <p className="mt-5 text-slate-300 text-lg">
            Cuatro claves para entender por qué operar a través de un extractor
            autorizado transforma tu cadena fiscal.
          </p>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 gap-5">
          {cards.map(({ icon: Icon, title, text, tag }, i) => (
            <motion.button
              type="button"
              key={title}
              onClick={() => setOpen(i)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`retef-card-${i}`}
              className="group relative text-left bg-gradient-to-br from-[#1E3A5F]/50 to-[#0A1628]/60 backdrop-blur-md border border-white/10 rounded-2xl p-8 hover:border-[#0066FF] hover:-translate-y-1 transition-all overflow-hidden cursor-pointer"
            >
              <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#0066FF]/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative flex items-start justify-between mb-5">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0066FF] to-[#1E3A5F] flex items-center justify-center">
                  <Icon className="w-7 h-7 text-white" strokeWidth={1.6} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#FF6B00] border border-[#FF6B00]/40 px-2.5 py-1 rounded-full">
                  {tag}
                </span>
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-white mb-3" style={{ fontFamily: "Poppins" }}>
                {title}
              </h3>
              <p className="text-slate-300 leading-relaxed">{text}</p>
              <span className="mt-4 inline-block text-sm font-semibold text-[#3B8AFF]">Saber más →</span>
            </motion.button>
          ))}
        </div>
      </div>

      {open !== null && (
        <Modal open={open !== null} onClose={() => setOpen(null)} title={cards[open].title} eyebrow="Marco regulatorio">
          {cards[open].modal}
        </Modal>
      )}
    </section>
  );
};

export default RetefInfo;
